import Axios, { AxiosError, AxiosResponse } from "axios";
import { authorizedAxios } from "../../Actions/AxiosActions";
import { RetailMatchGeographyOption } from "../../Enums/RetailMatchGeographyOption";
import { IBrandSampleSet } from "../../Models/IBrandSampleSet";
import { IRetailMatchGeography } from "../../Models/IRetailMatchGeography";
import {
    IBrand,
    IRetailMatchJob,
    IRetailMatchJobImage,
    IRetailMatchJobResult,
    ISegment,
    ISegmentDetails
} from "../../Models/Models";
import { IGetBrandsByGeographyAndCategoriesRequest } from "../../Models/Requests/IGetBrandsByGeographyAndCategoriesRequest";
import { IGetBrandsSampleSetDataByClosestRequest } from "../../Models/Requests/IGetBrandsSampleSetDataByClosestRequest";
import { IGetBrandsSampleSetDataByGeoRequest } from "../../Models/Requests/IGetBrandsSampleSetDataByGeoRequest";
import { ISubmitRetailMatchJobRequest } from "../../Models/Requests/ISubmitRetailMatchJobRequest";
import { IUpdateRetailMatchSiteDetailsRequest } from "../../Models/Requests/IUpdateRetailMatchSiteDetailsRequest";
import { IRetailMatchAxios } from "../index";

export class RetailMatchAxios implements IRetailMatchAxios {
    public GetBrandsByNaicsCode(naicsCode: string): Promise<IBrand[]> {
        return new Promise<IBrand[]>((resolve, reject) => {
            authorizedAxios("GET", `/services/gps/Brand/ByCategory/${naicsCode}`)
                .then((response: IBrand[]) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    }

    public GetBrandsByGeoAndCategory(
        request: IGetBrandsByGeographyAndCategoriesRequest
    ): Promise<IBrand[]> {
        return new Promise<IBrand[]>((resolve, reject) => {
            authorizedAxios(
                "POST",
                "/services/gps/Brand/ByGeoAndCategories",
                {},
                request
            )
                .then((response: IBrand[]) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    }

    public GetGeographies(
        option: RetailMatchGeographyOption
    ): Promise<IRetailMatchGeography[]> {
        return new Promise<IRetailMatchGeography[]>((resolve, reject) => {
            Axios.get(`/Common/GetRetailMatchGeographies?level=${option}`)
                .then((response: AxiosResponse) => {
                    resolve(response.data.data);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    }

    public UpdateJob(job: Partial<IRetailMatchJob>) {
        return new Promise<boolean>((resolve, reject) => {
            authorizedAxios("POST", "/services/gps/RetailMatch/UpdateJob", null, job)
                .then(() => resolve(true))
                .catch((error: AxiosError) => reject(error));
        });
    }

    public UpdateSiteDetails(jobId: string, siteDetails: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            const data: IUpdateRetailMatchSiteDetailsRequest = {
                jobId,
                siteDetails
            };
            authorizedAxios("POST", "/services/gps/RetailMatch/UpdateSiteDetails", null, data)
                .then(() => resolve(true))
                .catch((error: AxiosError) => reject(error));
        });
    }

    public AddImage(request: FormData) {
        return new Promise<IRetailMatchJobImage>((resolve, reject) => {
            authorizedAxios(
                "POST",
                "/services/gps/RetailMatch/AddImage",
                null,
                request
            )
                .then((response: IRetailMatchJobImage) => resolve(response))
                .catch((error: AxiosError) => reject(error));
        });
    }

    public UpdateBrandLogo(request: FormData): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            authorizedAxios(
                "POST",
                "/services/gps/RetailMatch/UpdateBrandLogo",
                null,
                request
            )
                .then((response: string) => resolve(response))
                .catch((error: AxiosError) => reject(error));
        });
    }

  public GetResults = (jobId: string): Promise<IRetailMatchJobResult> => {
      return new Promise<IRetailMatchJobResult>((resolve, reject) => {
          authorizedAxios("GET", `/services/gps/RetailMatch/${jobId}/results`)
              .then((response: IRetailMatchJobResult) => {
                  resolve(response);
              })
              .catch((error: AxiosError) => {
                  reject(error);
              });
      });
  };

  public SiteIsInDMA(latitude: number, longitude: number): Promise<boolean> {
      return new Promise<boolean>((resolve, reject) => {
          authorizedAxios(
              "GET",
              `/services/gps/RetailMatch/SiteIsInDMA/${latitude}/${longitude}`
          )
              .then((isInDMA: boolean) => {
                  resolve(isInDMA);
              })
              .catch((error: AxiosError) => {
                  reject(error);
              });
      });
  }

  public SubmitRetailMatchReport = (request: ISubmitRetailMatchJobRequest) => {
      return new Promise((resolve, reject) => {
          authorizedAxios("POST", "/services/gps/RetailMatch/submit", null, request)
              .then((response: AxiosResponse) => {
                  resolve(response);
              })
              .catch((error: AxiosError) => {
                  reject(error);
              });
      });
  };

  public GetMosaicSegments = (): Promise<ISegment[]> => {
      return new Promise((resolve, reject) => {
      /** this call does not need to be authorized */
          Axios.get("/Segment/GetSegments")
              .then((response: AxiosResponse) => {
                  resolve(response.data.data);
              })
              .catch((error: AxiosError) => {
                  reject(error);
              });
      });
  };

  public GetMosaicSegmentDetails = (): Promise<ISegmentDetails[]> => {
      return new Promise((resolve, reject) => {
      /** this call does not need to be authorized */
          Axios.get("/Segment/GetSegmentDetails")
              .then((response: AxiosResponse) => {
                  resolve(response.data.data);
              })
              .catch((error: AxiosError) => {
                  reject(error);
              });
      });
  };

  public GetBrandsSampleSetData = (
      request: IGetBrandsSampleSetDataByClosestRequest
  ): Promise<IBrandSampleSet[]> => {
      return new Promise((resolve, reject) => {
          authorizedAxios(
              "POST",
              "/services/gps/RetailMatch/GetSampleSetLocations",
              null,
              request
          )
              .then((response: IBrandSampleSet[]) => {
                  resolve(response);
              })
              .catch((error: AxiosError) => {
                  reject(error);
              });
      });
  };

  public GetSampleSetByGeo(
      request: IGetBrandsSampleSetDataByGeoRequest
  ): Promise<IBrandSampleSet[]> {
      return new Promise<IBrandSampleSet[]>((resolve, reject) => {
          authorizedAxios(
              "POST",
              "/services/gps/RetailMatch/GetSampleSetByGeo",
              null,
              request
          )
              .then((locations: IBrandSampleSet[]) => {
                  resolve(locations);
              })
              .catch((error: AxiosError) => {
                  reject(error);
              });
      });
  }
}
